import { useState, useEffect, useCallback } from 'react';
import {
  Landmark, Plus, RefreshCw, Link2, CreditCard, CheckCircle,
  AlertTriangle, Loader2, Settings, Trash2,
} from 'lucide-react';
import { usePlaidLink } from 'react-plaid-link';
import { colors, Card, Button } from '../../shared';
import { useData } from '../../../contexts/DataContext';
import {
  createLinkToken,
  exchangePublicToken,
  syncTransactions,
  getLinkedAccounts,
} from '../../../utils/plaidApi';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';

// ─── Plaid Link Wrapper ──────────────────────────────────────────────────────

function PlaidLinkButton({ userId, onSuccess, disabled }) {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchToken = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await createLinkToken(userId);
      setLinkToken(result.link_token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken, metadata) => {
      try {
        const result = await exchangePublicToken(publicToken);
        onSuccess(result.item_id, metadata);
      } catch (err) {
        setError(err.message);
      }
    },
    onExit: () => {},
  });

  // Auto-fetch token on mount
  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm" style={{ color: colors.red }}>
        <AlertTriangle size={16} />
        <span>{error}</span>
        <button onClick={fetchToken} className="underline" style={{ color: colors.yellow }}>Retry</button>
      </div>
    );
  }

  return (
    <Button
      onClick={() => open()}
      disabled={!ready || loading || disabled}
      className="flex items-center gap-2"
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
      Connect Bank Account
    </Button>
  );
}

// ─── Account Card ────────────────────────────────────────────────────────────

function AccountCard({ account, itemId, cards, onMapCard, onSync, syncing }) {
  const [selectedCard, setSelectedCard] = useState(account.cardLast4 || '');

  const handleMapCard = async () => {
    if (selectedCard !== account.cardLast4) {
      await onMapCard(itemId, account.account_id, selectedCard);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CreditCard size={18} style={{ color: colors.yellow }} />
          <div>
            <span className="font-semibold text-sm" style={{ color: colors.textPrimary }}>
              {account.official_name || account.name}
            </span>
            {account.mask && (
              <span className="text-xs ml-2" style={{ color: colors.textMuted }}>
                ••••{account.mask}
              </span>
            )}
          </div>
        </div>
        <span
          className="text-xs px-2 py-1 rounded-full"
          style={{ background: colors.cardBg, color: colors.textSecondary }}
        >
          {account.type} / {account.subtype}
        </span>
      </div>

      {/* Card mapping */}
      <div className="flex items-center gap-2 mt-2">
        <span className="text-xs" style={{ color: colors.textMuted }}>Map to card:</span>
        <select
          value={selectedCard}
          onChange={(e) => setSelectedCard(e.target.value)}
          className="text-xs px-2 py-1 rounded border"
          style={{
            background: colors.cardBg,
            color: colors.textPrimary,
            borderColor: colors.border,
          }}
        >
          <option value="">Unmapped</option>
          {cards.map((c) => (
            <option key={c.cardLast4} value={c.cardLast4}>
              {c.cardLast4} — {c.holderName || 'Unknown'}
            </option>
          ))}
        </select>
        {selectedCard !== (account.cardLast4 || '') && (
          <button
            onClick={handleMapCard}
            className="text-xs px-2 py-1 rounded"
            style={{ background: colors.yellowDim, color: colors.yellow }}
          >
            Save
          </button>
        )}
      </div>
    </Card>
  );
}

// ─── Connection Card ─────────────────────────────────────────────────────────

function ConnectionCard({ connection, cards, onSync, onMapCard, onRefreshAccounts }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getLinkedAccounts(connection.itemId);
      setAccounts(result.accounts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [connection.itemId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const result = await syncTransactions(connection.itemId);
      setSyncResult(result);
      onSync?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const lastSync = connection.lastSync
    ? (connection.lastSync.toDate ? connection.lastSync.toDate() : new Date(connection.lastSync))
    : null;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: colors.yellowDim }}
          >
            <Landmark size={20} style={{ color: colors.yellow }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>
              {connection.institutionName || 'Bank Account'}
            </h3>
            <p className="text-xs" style={{ color: colors.textMuted }}>
              {lastSync ? `Last synced: ${lastSync.toLocaleString()}` : 'Never synced'}
            </p>
          </div>
        </div>

        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all"
          style={{
            background: syncing ? colors.cardBg : colors.yellowDim,
            color: syncing ? colors.textMuted : colors.yellow,
          }}
        >
          {syncing ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <RefreshCw size={16} />
          )}
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm mb-3 p-2 rounded" style={{ background: colors.redDim, color: colors.red }}>
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}

      {syncResult && (
        <div className="flex items-center gap-2 text-sm mb-3 p-2 rounded" style={{ background: colors.greenDim, color: colors.green }}>
          <CheckCircle size={14} />
          <span>
            Synced: +{syncResult.added} added, {syncResult.modified} modified, {syncResult.removed} removed
          </span>
        </div>
      )}

      {/* Accounts list */}
      {loading ? (
        <div className="flex items-center gap-2 py-4" style={{ color: colors.textMuted }}>
          <Loader2 size={16} className="animate-spin" />
          <span className="text-sm">Loading accounts...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map((acct) => (
            <AccountCard
              key={acct.account_id}
              account={acct}
              itemId={connection.itemId}
              cards={cards}
              onMapCard={onMapCard}
              onSync={handleSync}
              syncing={syncing}
            />
          ))}
          {accounts.length === 0 && !error && (
            <p className="text-sm" style={{ color: colors.textMuted }}>No accounts found.</p>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Main BankingTab ─────────────────────────────────────────────────────────

export default function BankingTab({ permissions }) {
  const { getAllCards } = useData();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  const cards = getAllCards();

  const loadConnections = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'plaid_connections'));
      const conns = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setConnections(conns);
    } catch (err) {
      console.error('Failed to load Plaid connections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const handleLinkSuccess = async (itemId) => {
    // Reload connections after successful link
    await loadConnections();
  };

  const handleMapCard = async (itemId, accountId, cardLast4) => {
    try {
      const connRef = doc(db, 'plaid_connections', itemId);
      // Read current cardLast4Map and update
      const conn = connections.find((c) => c.itemId === itemId);
      const updatedMap = { ...(conn?.cardLast4Map || {}), [accountId]: cardLast4 };
      await updateDoc(connRef, { cardLast4Map: updatedMap });
      await loadConnections();
    } catch (err) {
      console.error('Failed to map card:', err);
    }
  };

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
            Banking
          </h1>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
            Connect bank accounts to automatically import transactions
          </p>
        </div>
        <PlaidLinkButton
          userId="admin"
          onSuccess={handleLinkSuccess}
          disabled={!permissions?.manageAdmins}
        />
      </div>

      {/* Connections list */}
      {loading ? (
        <div className="flex items-center justify-center py-12" style={{ color: colors.textMuted }}>
          <Loader2 size={24} className="animate-spin mr-3" />
          Loading connections...
        </div>
      ) : connections.length === 0 ? (
        <Card className="p-8 text-center">
          <Landmark size={48} style={{ color: colors.textMuted }} className="mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2" style={{ color: colors.textPrimary }}>
            No bank accounts connected
          </h3>
          <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
            Connect a bank account to start importing transactions automatically.
            Transactions will appear in the Receipts screen for matching.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {connections.map((conn) => (
            <ConnectionCard
              key={conn.id}
              connection={conn}
              cards={cards}
              onSync={loadConnections}
              onMapCard={handleMapCard}
              onRefreshAccounts={loadConnections}
            />
          ))}
        </div>
      )}

      {/* Info footer */}
      <div className="mt-6 p-4 rounded-xl" style={{ background: colors.cardBg, borderColor: colors.border }}>
        <h4 className="text-sm font-semibold mb-2" style={{ color: colors.textPrimary }}>
          How it works
        </h4>
        <ul className="text-xs space-y-1" style={{ color: colors.textMuted }}>
          <li>1. Connect a bank account using Plaid's secure widget</li>
          <li>2. Map each account to a company card (last 4 digits) so charges get assigned to the right tech</li>
          <li>3. Click "Sync Now" to pull latest transactions — they appear as bank charges in Receipts</li>
          <li>4. CSV import still works as a fallback for any accounts not connected through Plaid</li>
        </ul>
      </div>
    </div>
  );
}
